import React, { useRef, useEffect, useState, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import { LuPlus, LuListChecks } from "react-icons/lu";
import i18n from "i18next";
import DOMPurify from "dompurify";
import { safeDecodeURI, normalizeFontSizes } from "../../../../common/common_tools";
import { splitSlots, renumberSlots } from "../../../../common/dnd_slots";

/* eslint-disable react/prop-types */

// Lightweight 1-click inline editor for assessment question / answer text.
// A contentEditable that PRESERVES rich HTML (innerHTML) and commits on blur.
// The parent passes the display wrapper's className/style so the text renders
// pixel-identical while editing, and a click with no actual edit closes via
// onCancel — no state write, no undo entry. Formatting lives in the BasicText
// pill; the only floating extra here is FillInBlank's blank-answers button.
// DragDrop slots render as atomic chips (the same .dnd-slot-chip box) and
// serialize back to {{#N}} on commit, so the raw notation is never shown.

function placeCaretEnd(el) {
  try {
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  } catch (_) {
    /* selection may be unavailable */
  }
}

// Build the editable innerHTML for a DragDrop question: author text segments are
// DOMPurify-sanitized; each {{#N}} renders as a trusted atomic, non-editable chip.
export function buildDndInnerHtml(decodedNoP) {
  return splitSlots(decodedNoP)
    .map((part) =>
      part.type === "slot"
        ? `<span class="dnd-slot-chip" data-slot contenteditable="false">${part.index}</span>`
        : DOMPurify.sanitize(part.value)
    )
    .join("");
}

// Serialize a DragDrop editable back to "<p>…{{#1}} {{#2}}</p>": text kept,
// inline formatting preserved, chips → sequential {{#N}} in document order.
export function serializeDnd(el) {
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return "";
    if (node.classList && node.classList.contains("dnd-slot-chip")) return " {{#0}} ";
    if (node.tagName === "BR") return "";
    let inner = "";
    node.childNodes.forEach((c) => (inner += walk(c)));
    const tag = node.tagName.toLowerCase();
    if (tag === "p" || tag === "div") return inner; // flatten block wrappers; we re-wrap once
    const styleAttr = node.getAttribute && node.getAttribute("style");
    return `<${tag}${styleAttr ? ` style="${styleAttr}"` : ""}>${inner}</${tag}>`;
  };
  let out = "";
  el.childNodes.forEach((c) => (out += walk(c)));
  out = renumberSlots(out).replace(/ {2,}/g, " "); // tidy doubled spaces around chips
  return "<p>" + out + "</p>";
}

// FillInBlank blanks are inline "#----…----#" markers (no answer containers). We
// render each as an atomic chip and serialize back to "#----answer----#" — the
// marker's inner text is irrelevant (the Visor/editor replaces it with an input).
const FIB_RE = /#----(.*?)----#/g;

export function buildFibInnerHtml(decodedNoP) {
  const out = [];
  let last = 0;
  const re = new RegExp(FIB_RE.source, "g");
  let m;
  while ((m = re.exec(decodedNoP)) !== null) {
    out.push(DOMPurify.sanitize(decodedNoP.slice(last, m.index)));
    out.push('<span class="fib-blank-chip" data-blank contenteditable="false">blank</span>');
    last = m.index + m[0].length;
  }
  out.push(DOMPurify.sanitize(decodedNoP.slice(last)));
  return out.join("");
}

export function serializeFib(el) {
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return "";
    if (node.classList && node.classList.contains("fib-blank-chip")) return " #----answer----# ";
    if (node.tagName === "BR") return "";
    let inner = "";
    node.childNodes.forEach((c) => (inner += walk(c)));
    const tag = node.tagName.toLowerCase();
    if (tag === "p" || tag === "div") return inner;
    const styleAttr = node.getAttribute && node.getAttribute("style");
    return `<${tag}${styleAttr ? ` style="${styleAttr}"` : ""}>${inner}</${tag}>`;
  };
  let out = "";
  el.childNodes.forEach((c) => (out += walk(c)));
  out = out.replace(/ {2,}/g, " ");
  return "<p>" + out + "</p>";
}

// Chips (fib blank / dnd slot) are contenteditable="false" atomic nodes. Browsers
// PROTECT them: pressing Backspace/Delete next to one is a no-op, so a blank can't
// be removed by hand and the count never drops. Find the chip a Backspace (before)
// or Delete (after) would target from a collapsed caret, skipping one whitespace
// text node (serialize pads chips with spaces). Returns the chip node or null.
export function chipInDeletionDirection(range, before) {
  const isChip = (n) =>
    n &&
    n.nodeType === Node.ELEMENT_NODE &&
    n.classList &&
    (n.classList.contains("fib-blank-chip") || n.classList.contains("dnd-slot-chip"));
  let cand;
  const c = range.startContainer;
  const o = range.startOffset;
  if (c.nodeType === Node.TEXT_NODE) {
    // Mid-text: let the browser delete a real character.
    if (before ? o > 0 : o < c.textContent.length) return null;
    cand = before ? c.previousSibling : c.nextSibling;
  } else {
    cand = before ? c.childNodes[o - 1] : c.childNodes[o];
  }
  if (cand && cand.nodeType === Node.TEXT_NODE && cand.textContent.trim() === "") {
    cand = before ? cand.previousSibling : cand.nextSibling;
  }
  return isChip(cand) ? cand : null;
}

// Pasted markup must adopt the box's own font instead of carrying the source's.
// Browsers hand us the source's inline font-size/font-family, and the caret
// normally sits INSIDE an already styled span, so em-relative sizes nest and the
// text shrinks a little with every paste. Drop the font declarations (and <font>
// size/face) and keep everything else: bold/italic/links/lists/colors.
export function sanitizePastedHtml(html) {
  const holder = document.createElement("div");
  holder.innerHTML = DOMPurify.sanitize(html || "");
  holder.querySelectorAll("font").forEach((el) => {
    el.removeAttribute("size");
    el.removeAttribute("face");
  });
  holder.querySelectorAll("[style]").forEach((el) => {
    el.style.removeProperty("font-size");
    el.style.removeProperty("font-family");
    el.style.removeProperty("line-height");
    if (!el.getAttribute("style")) el.removeAttribute("style");
  });
  return holder.innerHTML;
}

function insertHtmlAtCaret(html) {
  try {
    if (document.execCommand("insertHTML", false, html)) return;
  } catch (_) {
    /* fall through to the Range path */
  }
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return;
  const range = sel.getRangeAt(0);
  range.deleteContents();
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  const last = tpl.content.lastChild;
  range.insertNode(tpl.content);
  if (last) {
    range.setStartAfter(last);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }
}

export function InlineHtmlEditor({
  html,
  dndQuestion,
  fibQuestion,
  onCommit,
  onCancel,
  onAddDragArea,
  fibAnswers,
  onSetFibAnswer,
  style,
  className,
}) {
  const ref = useRef(null);
  const rootRef = useRef(null);
  const doneRef = useRef(false);
  const commitRef = useRef(null);
  const menuRef = useRef(null);
  const caretBtnRef = useRef(null);
  // Serialized snapshot taken right after seeding — commit compares against it
  // so an edit-less open/close is a pure no-op (browser/sanitizer normalization
  // alone never counts as a change).
  const initialSerializedRef = useRef(null);
  const [showFibPopup, setShowFibPopup] = useState(false);
  // Screen position of the text caret — the small "+" button floats just above
  // it (where the user clicked), not at the box corner.
  const [caretPos, setCaretPos] = useState(null);
  // Number of blanks = count of stored "#----…----#" markers in the question.
  const fibBlankCount = fibQuestion
    ? (safeDecodeURI(html || "").match(/#----.*?----#/g) || []).length
    : 0;
  // The mini menu is PORTALED to <body> (fixed) so the box's overflow:hidden
  // ancestors (.exercisePlugin/.boxStyle) can't clip it. Track its screen anchor.
  const [menuPos, setMenuPos] = useState(null);

  // Commit + close on a pointerdown OUTSIDE the editor (incl. clicking another
  // box). This is focus-independent — unlike onBlur it always fires, so the
  // editor never gets stuck open and clicking another element auto-closes this
  // one (matching AtomicBox's outside-click handling). The window listener is
  // added once; it calls the latest commit via commitRef. The portaled menu is
  // outside rootRef in the DOM, so exclude it explicitly.
  useEffect(() => {
    const onDocDown = (e) => {
      const root = rootRef.current;
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      // The caret "+" is portaled to <body> too — exclude it or clicking it
      // would count as an outside-click and commit/close before its onClick runs.
      const inCaretBtn = caretBtnRef.current && caretBtnRef.current.contains(e.target);
      if (root && !root.contains(e.target) && !inMenu && !inCaretBtn && commitRef.current)
        commitRef.current();
    };
    document.addEventListener("pointerdown", onDocDown, true);
    return () => document.removeEventListener("pointerdown", onDocDown, true);
  }, []);

  // Anchor the portaled mini menu just BELOW the editor's bottom-right (the
  // BasicText formatting pill owns the strip above the box); keep it in sync
  // on scroll/resize so it follows the canvas.
  useLayoutEffect(() => {
    const place = () => {
      const root = rootRef.current;
      if (!root) return;
      const r = root.getBoundingClientRect();
      setMenuPos({ top: r.bottom + 6, right: Math.max(2, window.innerWidth - r.right) });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [html]);

  // Seed from the stored __text. Re-runs when `html` changes externally (e.g.
  // after "+ drag area" appends a slot) but NOT on local keystrokes (those don't
  // change the prop), so typing is never clobbered.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // normalizeFontSizes matches the display path (EditorBox.renderChildren),
    // which converts px font-sizes to canvas-relative em — without it the text
    // visibly resizes the moment editing starts.
    const decoded = normalizeFontSizes(safeDecodeURI(html || ""));
    if (dndQuestion) {
      el.innerHTML = buildDndInnerHtml(decoded.replace(/<\/?p>/g, ""));
    } else if (fibQuestion) {
      el.innerHTML = buildFibInnerHtml(decoded.replace(/<\/?p>/g, ""));
    } else {
      el.innerHTML = DOMPurify.sanitize(decoded) || "<br/>";
    }
    el.focus();
    placeCaretEnd(el);
    updateCaret();
    doneRef.current = false;
    initialSerializedRef.current = serialize();
  }, [html, dndQuestion, fibQuestion]);

  // Track the caret's screen rect so the "+" button can sit right above it.
  // Collapsed carets can report an empty rect (esp. at a node boundary) — fall
  // back to the first client rect, then to the editable's top-left.
  const updateCaret = () => {
    const el = ref.current;
    if (!el) return;
    const sel = window.getSelection();
    let rect = null;
    if (sel && sel.rangeCount && el.contains(sel.anchorNode)) {
      const range = sel.getRangeAt(0).cloneRange();
      if (typeof range.getBoundingClientRect === "function") {
        rect = range.getBoundingClientRect();
        if (!rect || (rect.top === 0 && rect.left === 0 && rect.height === 0)) {
          const rects = range.getClientRects ? range.getClientRects() : null;
          if (rects && rects.length) rect = rects[0];
        }
      }
    }
    // No usable caret rect (collapsed at a boundary, or unsupported) → anchor to
    // the editable's top-left so the "+" still appears.
    if (!rect || (rect.top === 0 && rect.height === 0)) {
      const ar = el.getBoundingClientRect();
      rect = { top: ar.top, left: ar.left };
    }
    setCaretPos({ top: rect.top, left: rect.left });
  };

  const serialize = () => {
    const el = ref.current;
    if (!el) return html;
    if (dndQuestion) return serializeDnd(el);
    if (fibQuestion) return serializeFib(el);
    return el.innerHTML;
  };

  const commit = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    const out = serialize();
    // Nothing actually changed → close silently. Committing here would still
    // rewrite __text (sanitize/normalize churn), dirtying the project and
    // pushing an undo entry from a mere click.
    if (initialSerializedRef.current !== null && out === initialSerializedRef.current) {
      onCancel();
      return;
    }
    onCommit(out);
  };
  commitRef.current = commit;

  const handleKeyDown = (e) => {
    e.stopPropagation(); // never let box-level shortcuts fire while editing
    if (e.key === "Escape") {
      e.preventDefault();
      doneRef.current = true;
      onCancel();
      return;
    }
    // Manually delete a blank/slot chip: the browser won't (atomic), so remove it
    // ourselves. On commit the marker count drops and nBoxes re-syncs.
    if ((e.key === "Backspace" || e.key === "Delete") && (dndQuestion || fibQuestion)) {
      const sel = window.getSelection();
      if (sel && sel.rangeCount) {
        const range = sel.getRangeAt(0);
        if (range.collapsed) {
          const chip = chipInDeletionDirection(range, e.key === "Backspace");
          if (chip) {
            e.preventDefault();
            // Keep at least one blank/slot — the stepper's min is 1, so text-
            // deleting the last one would desync the count. Block it.
            const selector = fibQuestion ? ".fib-blank-chip" : ".dnd-slot-chip";
            const remaining = ref.current ? ref.current.querySelectorAll(selector).length : 0;
            if (remaining <= 1) return;
            chip.remove();
            updateCaret();
          }
        }
      }
    }
  };

  const handlePaste = (e) => {
    const cd = e.clipboardData;
    if (!cd) return;
    // Own the paste: the document-level Clipboard listener would otherwise read
    // it as a canvas paste and spawn a new box.
    e.preventDefault();
    e.stopPropagation();
    const rawHtml = cd.getData("text/html");
    if (rawHtml) {
      const clean = sanitizePastedHtml(rawHtml);
      if (clean) insertHtmlAtCaret(clean);
    } else {
      const text = cd.getData("text");
      if (text) document.execCommand("insertText", false, text);
    }
    updateCaret();
  };

  // Keep focus in the editable when a mini-menu button is pressed (so onBlur
  // doesn't commit/close before the button's onClick runs).
  const keepFocus = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Floor for the caret "+" button's top, in viewport px, so it never renders
  // on top of the box's own formatting pill (AssessmentBoxMenu/TopActionPill).
  // That pill is anchored to the box's top-right corner and — per
  // EditorShortcuts.resize()'s Math.max(4, ...) clamp — can sit flush against
  // (or even overlap into) the box when there's no room above it, which is
  // exactly the case for a question box pinned to the top of the canvas. Only
  // applies the floor when the button's horizontal position would actually sit
  // under the pill; returns -Infinity (no constraint) otherwise/when no pill
  // is mounted.
  const pillBottomClearance = (caretLeft) => {
    const pillEl = document.querySelector("#topPillRoot .am-pill");
    if (!pillEl) return -Infinity;
    const pr = pillEl.getBoundingClientRect();
    const btnLeft = Math.max(2, caretLeft - 10);
    const btnRight = btnLeft + 20; // matches .ihe-caret-add's fixed 20px width
    if (btnRight <= pr.left || btnLeft >= pr.right) return -Infinity;
    return pr.bottom + 6;
  };

  // Insert a new slot/blank chip AT THE CARET (not at the end). Returns the
  // chip's document-order position among chips of its kind, so EditorApp can put
  // the new (empty) answer at the matching reading position instead of the end.
  const insertChipAtCaret = () => {
    const el = ref.current;
    if (!el) return 0;
    const chip = document.createElement("span");
    if (dndQuestion) {
      chip.className = "dnd-slot-chip";
      chip.setAttribute("data-slot", "");
      chip.textContent = "0";
    } else {
      chip.className = "fib-blank-chip";
      chip.setAttribute("data-blank", "");
      chip.textContent = "blank";
    }
    chip.setAttribute("contenteditable", "false");
    const sel = window.getSelection();
    const frag = document.createDocumentFragment();
    frag.appendChild(document.createTextNode(" "));
    frag.appendChild(chip);
    const tail = document.createTextNode(" ");
    frag.appendChild(tail);
    if (sel && sel.rangeCount && el.contains(sel.anchorNode)) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(frag);
      range.setStartAfter(tail);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      el.appendChild(frag);
    }
    const selector = dndQuestion ? ".dnd-slot-chip" : ".fib-blank-chip";
    const chips = Array.from(el.querySelectorAll(selector));
    const idx = chips.indexOf(chip);
    return idx < 0 ? chips.length - 1 : idx;
  };

  // A drag-select that starts in the editor but is released outside it (past
  // the end of the text, onto the answer row / next row) makes the browser fire
  // `click` on the COMMON ANCESTOR — the parent assessment box — whose onClick
  // then selects itself: the answer loses selection and its formatting pill
  // vanishes mid-selection. Swallow that one trailing click (capture phase, so
  // it never reaches React's root listener) when it lands outside the editor.
  const swallowDragSelectClick = () => {
    const onUp = () => {
      document.removeEventListener("mouseup", onUp, true);
      const swallow = (ev) => {
        const root = rootRef.current;
        if (root && !root.contains(ev.target)) {
          ev.stopPropagation();
          ev.preventDefault();
        }
      };
      document.addEventListener("click", swallow, true);
      // `click` is dispatched right after `mouseup` in the same input task.
      setTimeout(() => document.removeEventListener("click", swallow, true), 0);
    };
    document.addEventListener("mouseup", onUp, true);
  };

  return (
    <div
      ref={rootRef}
      // The display wrapper's className/style are passed through so the text
      // keeps the exact same font/padding/alignment while being edited.
      className={"inline-html-editor" + (className ? " " + className : "")}
      style={style}
      onMouseDown={(e) => {
        e.stopPropagation();
        // Portaled menu / caret "+" bubble here through React too — only arm
        // for presses that are really inside the editor's DOM.
        if (rootRef.current && rootRef.current.contains(e.target)) swallowDragSelectClick();
      }}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {menuPos && fibQuestion && onSetFibAnswer
        ? ReactDOM.createPortal(
            <div
              ref={menuRef}
              className="inline-html-editor__mini"
              contentEditable={false}
              style={{ position: "fixed", top: menuPos.top, right: menuPos.right }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className={"ihe-mini-btn ihe-mini-fibans" + (showFibPopup ? " is-open" : "")}
                title={i18n.t("AssessmentMenu.setBlankAnswers")}
                aria-label={i18n.t("AssessmentMenu.setBlankAnswers")}
                aria-expanded={showFibPopup}
                onMouseDown={keepFocus}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFibPopup((v) => !v);
                }}
              >
                <LuListChecks size={14} />
              </button>
              {showFibPopup ? (
                <div className="ihe-fib-popup" onMouseDown={(e) => e.stopPropagation()}>
                  <div className="ihe-fib-popup__title">
                    {i18n.t("AssessmentMenu.correctAnswers")}
                  </div>
                  {fibBlankCount === 0 ? (
                    <div className="ihe-fib-popup__empty">
                      {i18n.t("AssessmentMenu.noBlanksYet")}
                    </div>
                  ) : (
                    Array.from({ length: fibBlankCount }).map((_, i) => (
                      <label className="ihe-fib-row" key={i}>
                        <span className="ihe-fib-row__lbl">
                          {i18n.t("AssessmentMenu.blank")} {i + 1}
                        </span>
                        <input
                          type="text"
                          className="ihe-fib-row__input"
                          defaultValue={(fibAnswers && fibAnswers[i]) || ""}
                          placeholder={i18n.t("AssessmentMenu.correctValue")}
                          onMouseDown={(e) => e.stopPropagation()}
                          onChange={(e) => onSetFibAnswer(i, e.target.value)}
                        />
                      </label>
                    ))
                  )}
                </div>
              ) : null}
            </div>,
            document.body
          )
        : null}
      {/* Small "+" that floats just above the caret (where the user clicked).
          DragDrop → inserts a drag slot + adds a matching answer below;
          FillInBlank → inserts a blank only. When the caret sits on the box's
          first line, "24px above the caret" can land inside the box's own
          formatting pill (AssessmentBoxMenu/TopActionPill, anchored to the
          box's top-right corner) — that pill can itself be flush against the
          box when there's no room above it (see EditorShortcuts.resize()'s
          Math.max(4, ...) clamp). Clamp the button below the pill's bottom
          edge (when the two horizontally overlap) so they never stack. */}
      {(dndQuestion || fibQuestion) && onAddDragArea && caretPos
        ? ReactDOM.createPortal(
            <button
              ref={caretBtnRef}
              type="button"
              className="ihe-caret-add"
              title={
                dndQuestion
                  ? i18n.t("AssessmentMenu.addDragArea")
                  : i18n.t("AssessmentMenu.addFillBlank")
              }
              aria-label={
                dndQuestion
                  ? i18n.t("AssessmentMenu.addDragArea")
                  : i18n.t("AssessmentMenu.addFillBlank")
              }
              style={{
                position: "fixed",
                top: Math.max(2, pillBottomClearance(caretPos.left), caretPos.top - 24),
                left: Math.max(2, caretPos.left - 10),
              }}
              onMouseDown={keepFocus}
              onClick={(e) => {
                e.stopPropagation();
                const insertIndex = insertChipAtCaret();
                onAddDragArea(serialize(), insertIndex);
              }}
            >
              <LuPlus size={13} />
            </button>,
            document.body
          )
        : null}
      <div
        ref={ref}
        className="inline-html-editor__area"
        contentEditable
        suppressContentEditableWarning
        onBlur={(e) => {
          // Don't commit/close when focus moves into our own mini menu / popup
          // (e.g. clicking a "set blank answers" input) — only on a real focus-out.
          if (menuRef.current && menuRef.current.contains(e.relatedTarget)) return;
          commit();
        }}
        onKeyUp={updateCaret}
        onMouseUp={updateCaret}
        onInput={updateCaret}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
      />
    </div>
  );
}

export default InlineHtmlEditor;
